import React, { useCallback, useEffect, useMemo } from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import constants from 'constants/constants';
import moment from 'moment-timezone';
import { Link, useLocation, useParams } from 'react-router-dom';

import { useBaseTable } from 'hooks/useBaseTable';
import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useHashmapConverter from 'hooks/useHashmapConverter';
import { useLogicCheckerFilterHook } from 'hooks/useLogicCheckerFilterHook';
import useUserHook from 'hooks/useUserHook';

import { useValidationStore } from 'store/validation.store';

import { useMutationSetCheckMandatory } from 'api/mutations/setCheckMandatory.mutation';
import { useQueryGetLogicCheckerCategoryResults } from 'api/queries/getLogicCheckerCategoryResults.query';
import { useQueryGetLogicCheckerConfiguration } from 'api/queries/getLogicCheckerConfiguration.query';
import { useQueryGetValidationTypeDrillDown } from 'api/queries/getValidationTypeDrillDown.query';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { Switch } from 'components/DesignSystem/Switch';
import { LogicQualityCheckCardWidget } from 'components/widgets/LogicQualityCheckCardWidget/LogicQualityCheckCardWidget';
import classes from 'components/widgets/ValidationTable/ValidationTable.module.scss';
import { getComponentKeyByTitle, initialStateHelper } from 'components/widgets/ValidationTable/ValidationTable.utils';
import ValidationConfigSlider from 'components/widgets/ValidationTable/components/ValidationConfigSlider/ValidationConfigSlider';
import { ValidationPageCardModal } from 'components/widgets/ValidationTable/components/ValidationPageCardModal/validationPageCardModal';
import { useGetValidationTableUiStateForReport } from 'components/widgets/ValidationTable/hooks/useGetValidationTableUiStateForReport';
import { WholeProjectRowWrapper } from 'components/widgets/WholeProjectRow/wholeProjectRowWrapper';

import { getCheck, getValidationColumns, isWholeProject } from 'utilitys/logicQualityCheck.utils';

export const ValidationTable = () => {
    const { pathname, search } = useLocation();
    const { ability } = useUserHook();
    const { handleSnackBar } = useCustomSnackBar();
    const { validation, update } = useValidationStore();

    const {
        id: versionIdUrlParam,
        category: categoryUrlParam,
        check: checkUrlParam,
    } = useParams<{ id: string; category: string; check: string }>();
    const { idList, versionList } = useHashmapConverter(versionIdUrlParam);
    const projectId = versionList[0]?.projectId;
    const contractId = versionList[0]?.contractId;
    const fileMetaDataId = idList.length > 0 ? parseInt(idList.join(',')) : undefined;
    const uiStateForReport = useGetValidationTableUiStateForReport();

    const filters = useLogicCheckerFilterHook();
    const GetLogicCheckerCategoryResultsPayload = {
        fileMetaDataId,
        filter: {
            filters,
        },
    };
    const { data: logicCheckerData, isLoading: logicCheckerDataLoading } = useQueryGetLogicCheckerCategoryResults(
        GetLogicCheckerCategoryResultsPayload,
    );

    const { data: configuration, refetch: refetchConfig } = useQueryGetLogicCheckerConfiguration({
        contractId,
    });

    const enableManage = ability.can('view', 'LogicCheckerManager') || ability.can('all', 'Admin');

    const { mutate: setCheckMandatory, isLoading } = useMutationSetCheckMandatory();
    const parsedCategory = categoryUrlParam.replace('_', '/');
    const parsedCheck = checkUrlParam ? checkUrlParam.replace('_', '/') : '';
    const mappedComponentKeyForReport = getComponentKeyByTitle(parsedCheck);
    const { endpoint } = widgetsConfigurations[mappedComponentKeyForReport];
    const subTitle = useCustomPageSubTitle({ projectId, contractId, latestVersionId: idList[0] });

    const pickedCheck = getCheck({
        categoriesSummaryRows: logicCheckerData?.categoriesSummaryRows,
        parsedCategory,
        parsedCheck,
    });

    useEffect(() => {
        if (configuration) {
            const validation = configuration.find((item) => item.validationName === parsedCheck);
            const prevValue = validation?.thresholds[0].upper_limit_value || 0;
            update({ validation, prevValue });
        }
        return () => {
            update({ validation: undefined, prevValue: undefined });
        };
    }, [configuration, parsedCheck]);

    const { data: validationTypeDrillDown, isLoading: validationTypeDrillDownLoading } =
        useQueryGetValidationTypeDrillDown({
            fileMetaDataId,
            componentId: pickedCheck?.validationId,
            filter: {
                filters: filters,
            },
        });

    const toggleSwitch = useCallback(() => {
        if (pickedCheck?.validationId && validation && fileMetaDataId) {
            setCheckMandatory(
                { checkId: pickedCheck?.validationId, mandatory: !validation.mandatory, metaDataId: fileMetaDataId },
                {
                    onSuccess: () => {
                        refetchConfig().then(() => {
                            handleSnackBar('Config saved successfully.', 'success');
                        });
                    },
                    onError: (error) => {
                        const message = 'Something went wrong saving the config.';
                        DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
                    },
                },
            );
        }
    }, [pickedCheck, validation]);

    const generatedCols = useMemo(() => {
        return getValidationColumns({ validationTypeDrillDown, validation, check: checkUrlParam });
    }, [validationTypeDrillDown, checkUrlParam, validation]);

    const generatedData = useMemo(() => {
        return validationTypeDrillDown?.rows.filter(isWholeProject) || [];
    }, [validationTypeDrillDown]);

    const showLoader = useMemo(() => {
        return logicCheckerDataLoading || validationTypeDrillDownLoading;
    }, [logicCheckerDataLoading, validationTypeDrillDownLoading]);

    const initialState = useMemo(() => {
        return initialStateHelper({
            generatedCols,
            parsedCheck,
        });
    }, [generatedCols]);

    const versionName = `${versionList[0]?.versionName} (${moment(versionList[0]?.versionDate).format(
        constants.formats.date.default,
    )})`;

    const cardLink = useMemo(() => {
        return (
            <Link
                to={{
                    pathname: `${pathname}/card-${pickedCheck?.associatedCard?.cardId || 'newCard'}`,
                    search: search,
                }}
            >
                {parsedCheck}
            </Link>
        );
    }, [pickedCheck, pathname]);

    const titleWithLink = (
        <>
            {parsedCategory} | {cardLink}
        </>
    );

    const titleAsString = `${parsedCategory} | ${parsedCheck}`;

    const { table } = useBaseTable({
        componentKey: mappedComponentKeyForReport,
        initialState: initialState,
        columns: generatedCols,
    });

    return (
        <>
            <WidgetWithTitle
                title={titleWithLink}
                titleComponents={[
                    <div className={classes.cardWidgetWrapper} key={'CardWidget'}>
                        <LogicQualityCheckCardWidget
                            projectId={projectId}
                            contractId={contractId}
                            versionName={versionName}
                            cardId={pickedCheck?.associatedCard.cardId}
                            status={pickedCheck?.associatedCard.status}
                            assignee={pickedCheck?.associatedCard.assignee}
                            title={parsedCheck}
                            metaDataId={fileMetaDataId}
                            checkId={pickedCheck?.validationId}
                        />
                    </div>,
                    validation && (
                        <div className={classes.switchWrapper}>
                            <Switch
                                disabled={!enableManage || isLoading}
                                checked={validation?.mandatory}
                                onChange={toggleSwitch}
                                label={'Include in overall score'}
                            />
                        </div>
                    ),
                    <React.Fragment key={'ValidationConfigSlider'}>
                        {validation && fileMetaDataId && (
                            <ValidationConfigSlider disabled={!enableManage} fileMetaDataId={fileMetaDataId} />
                        )}
                    </React.Fragment>,
                    mappedComponentKeyForReport && (
                        <AddToReport
                            key={'addWidgetToReport'}
                            componentKey={mappedComponentKeyForReport}
                            projectId={projectId}
                            contractId={contractId}
                            uiState={uiStateForReport}
                            shouldUseNewIcon={true}
                            apiPayload={GetLogicCheckerCategoryResultsPayload}
                            widgetEndpoint={endpoint}
                        />
                    ),

                    <ExportDropdown key={'export'} title={titleAsString} subTitle={subTitle} />,
                ]}
            >
                <TableContainer>
                    <WholeProjectRowWrapper columns={generatedCols} check={parsedCheck} />
                    <div className={classes.validationDescription}>
                        <h3>Validation overview</h3>
                        <p>{validationTypeDrillDown?.validationDescription}</p>
                    </div>
                    <TableV8
                        columns={generatedCols}
                        data={generatedData}
                        initialState={initialState}
                        isLoading={showLoader}
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        manualSorting={false}
                    />
                </TableContainer>
            </WidgetWithTitle>
            <ValidationPageCardModal />
        </>
    );
};
